/**
 * Projects Section Component
 * Migrated to TypeScript with subcomponents
 */

import React, { useEffect, useRef, useState } from 'react';
import { FaGithub, FaExternalLinkAlt, FaCode, FaCalendarAlt, FaJava, FaDatabase, FaChartLine } from 'react-icons/fa';
import { SiPython, SiPytorch, SiNumpy, SiPandas, SiJupyter, SiApachehadoop, SiApachespark, SiCplusplus, SiUbuntu, SiWireshark, SiHtml5, SiCss3, SiJavascript, SiPhp, SiMysql, SiMongodb, SiReact, SiNextdotjs, SiTypescript, SiVercel, SiPostgresql, SiScikitlearn, SiStreamlit, SiAwsamplify } from 'react-icons/si';
import { GiArtificialIntelligence, GiBrain, GiNetworkBars, GiProcessor } from 'react-icons/gi';
import { MdSecurity } from 'react-icons/md';
import { VscGithub } from 'react-icons/vsc';
import { BsShieldLock, BsGraphUp } from 'react-icons/bs';
import { 
  LambdaIcon, APIGatewayIcon, IAMIcon, S3Icon, ThreadsIcon, SynchronizationIcon,
  VirtualizationIcon, SystemCallsIcon, VirtualMemoryIcon, SHA256Icon, CryptographyIcon,
  ResNetIcon, DenseNetIcon, VGGIcon, GoogleNetIcon, LinearRegressionIcon, NLPIcon,
  GraphAPIIcon, BTreeIcon, QueryOptimizationIcon, EmbeddedCIcon, MultithreadingIcon,
  CloudComputingIcon, FinpyIcon
} from '../../components/icons/CustomTechIcons';
import { projects } from '../../data/projects';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import ProjectModal from '../../components/ProjectModal';
import { projectCategories } from '../../config/projectCategories';

const Projects: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState(projects);
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { getColorClasses } = useColorScheme();
  const colors = getColorClasses();

  const categories = ['All', ...new Set(projects.map(project => project.category))];

  const getTechIcon = (techName: string) => {
    const iconMap: Record<string, any> = {
      'numpy': SiNumpy,
      'pandas': SiPandas,
      'Pytorch': SiPytorch,
      'jupyter': SiJupyter,
      'Hadoop': SiApachehadoop,
      'Pyspark': SiApachespark,
      'C': SiCplusplus,
      'Python': SiPython,
      'Ubuntu': SiUbuntu,
      'Wireshark': SiWireshark,
      'HTML': SiHtml5,
      'CSS': SiCss3,
      'JavaScript': SiJavascript,
      'JavaScript (DBMS)': SiJavascript,
      'PHP': SiPhp,
      'SQL': SiMysql,
      'NoSQL': SiMongodb,
      'Image Classification': GiArtificialIntelligence,
      'Machine Learning': GiArtificialIntelligence,
      'Deep Learning': GiBrain,
      'CNN': GiArtificialIntelligence,
      'React': SiReact,
      'Next.js': SiNextdotjs,
      'TypeScript': SiTypescript,
      'Vercel': SiVercel,
      'GitHub API': VscGithub,
      'Analytics': FaChartLine,
      'Data Visualization': BsGraphUp,
      'Java': FaJava,
      'PostgreSQL': SiPostgresql,
      'Relational Database': FaDatabase,
      'Query Optimization': QueryOptimizationIcon,
      'B+ Tree': BTreeIcon,
      'Multithreading': MultithreadingIcon,
      'Map-reduce': GiProcessor,
      'matplotlib': BsGraphUp,
      'sns': BsGraphUp,
      'scikit': SiScikitlearn,
      'streamlit': SiStreamlit,
      'Cybersecurity': BsShieldLock,
      'Network Security': GiNetworkBars,
      'Web Security': MdSecurity,
      'Cryptography': CryptographyIcon,
      // AWS Services
      'AWS Amplify': SiAwsamplify,
      'Lambda': LambdaIcon,
      'API Gateway': APIGatewayIcon,
      'DynamoDB': FaDatabase,
      'IAM': IAMIcon,
      'S3': S3Icon,
      'SHA-256': SHA256Icon,
      'Cloud Computing': CloudComputingIcon,
      // System Concepts
      'Threads': ThreadsIcon,
      'Synchronization': SynchronizationIcon,
      'Virtualization': VirtualizationIcon,
      'Operating Systems': GiProcessor,
      'System Calls': SystemCallsIcon,
      'Virtual Memory': VirtualMemoryIcon,
      // Neural Networks
      'Resnet18': ResNetIcon,
      'DenseNet121': DenseNetIcon,
      'GoogleNet': GoogleNetIcon,
      'VGG16': VGGIcon,
      'ResNeXt': ResNetIcon,
      'ResNet50': ResNetIcon,
      // Data Science
      'Graph API': GraphAPIIcon,
      'NLP': NLPIcon,
      'Linear Regression': LinearRegressionIcon,
      'finpy': FinpyIcon,
      // Programming
      'Embedded C': EmbeddedCIcon,
      'MATLAB (Image Processing Toolbox)': BsGraphUp
    };
    return iconMap[techName] || FaCode;
  };

  const getTechColor = (techName: string) => {
    const colorMap: Record<string, string> = {
      'numpy': '#4D77CF',
      'pandas': '#150458',
      'Pytorch': '#EE4C2C',
      'jupyter': '#F37626',
      'Hadoop': '#FF6600',
      'Pyspark': '#E25A1C',
      'C': '#A8B9CC',
      'Python': '#3776AB',
      'Ubuntu': '#E95420',
      'Wireshark': '#1679A7',
      'HTML': '#E34F26',
      'CSS': '#1572B6',
      'JavaScript': '#F7DF1E',
      'JavaScript (DBMS)': '#F7DF1E',
      'PHP': '#777BB4',
      'SQL': '#4479A1',
      'NoSQL': '#47A248',
      'Image Classification': '#FF6B6B',
      'Machine Learning': '#FF6B6B',
      'Deep Learning': '#4ECDC4',
      'CNN': '#FF6B6B',
      'React': '#61DAFB',
      'Next.js': '#000000',
      'TypeScript': '#3178C6',
      'Vercel': '#000000',
      'GitHub API': '#181717',
      'Analytics': '#4A90E2',
      'Data Visualization': '#F39C12',
      'Java': '#007396',
      'PostgreSQL': '#336791',
      'Relational Database': '#336791',
      'Query Optimization': '#4A90E2',
      'B+ Tree': '#8B7355',
      'Multithreading': '#F39C12',
      'Map-reduce': '#E25A1C',
      'matplotlib': '#11557C',
      'sns': '#F39C12',
      'scikit': '#F7931E',
      'streamlit': '#FF4B4B',
      'Cybersecurity': '#FF0000',
      'Network Security': '#FF6600',
      'Web Security': '#DC3545',
      'Cryptography': '#FFD700',
      // AWS Services
      'AWS Amplify': '#FF9900',
      'Lambda': '#FF9900',
      'API Gateway': '#FF4F8B',
      'DynamoDB': '#4053D6',
      'IAM': '#DD344C',
      'S3': '#569A31',
      'SHA-256': '#FFD700',
      'Cloud Computing': '#4A90E2',
      // System Concepts
      'Threads': '#E25A1C',
      'Synchronization': '#4A90E2',
      'Virtualization': '#00D8FF',
      'Operating Systems': '#5C5C5C',
      'System Calls': '#F39C12',
      'Virtual Memory': '#9B59B6',
      // Neural Networks
      'Resnet18': '#EE4C2C',
      'DenseNet121': '#4ECDC4',
      'GoogleNet': '#4285F4',
      'VGG16': '#FF6B6B',
      'ResNeXt': '#EE4C2C',
      'ResNet50': '#EE4C2C',
      // Data Science
      'Graph API': '#0088CC',
      'NLP': '#4A90E2',
      'Linear Regression': '#3498DB',
      'finpy': '#2ECC71',
      // Programming
      'Embedded C': '#A8B9CC',
      'MATLAB (Image Processing Toolbox)': '#FF8800'
    };
    return colorMap[techName] || '#6C757D';
  };

  const getCategoryIcon = (category: string) => {
    return projectCategories[category as keyof typeof projectCategories]?.icon || FaCode;
  };

  const handleFilter = (category: string) => {
    setActiveFilter(category);
    if (category === 'All') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(project => project.category === category));
    }
  };

  const openModal = (project: any) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="projects" ref={sectionRef} className="py-20">
      <div className="container mx-auto px-4">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="accent-gradient-text">
                Projects
              </span>
            </h2>
            <p className={`text-xl ${colors.textSecondary} max-w-3xl mx-auto`}>
              Showcase of my work and technical projects
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => {
              const Icon = getCategoryIcon(category);
              return (
                <button
                  key={category}
                  onClick={() => handleFilter(category)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                    activeFilter === category
                      ? `${colors.bg} text-white shadow-lg`
                      : `${colors.textSecondary} ${colors.hoverAccent} hover:${colors.bg} hover:text-white`
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{category}</span>
                </button>
              );
            })}
          </div>

          {/* Projects Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className={`group ${colors.cardBackground} rounded-2xl overflow-hidden shadow-lg border border-gray-300 dark:border-gray-700 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer`}
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => openModal(project)}
              >
                {/* Project Image */}
                <div className="relative h-48 overflow-hidden">
                  {project.image && project.image.includes('/leetcode/') ? (
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 accent-gradient-bg-subtle flex items-center justify-center">
                      <FaCode className="w-16 h-16" style={{ color: 'var(--color-primary)' }} />
                    </div>
                  )}
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 ${colors.bg} text-gray-900 text-xs font-semibold rounded-full flex items-center space-x-1`}>
                      {React.createElement(getCategoryIcon(project.category), { className: "w-3 h-3" })}
                      <span>{project.category}</span>
                    </span>
                  </div>
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      project.status === 'Completed' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                </div>

                {/* Project Content */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`text-xl font-bold text-gray-900 dark:text-white group-hover:${colors.hoverAccent} transition-colors duration-300`}>
                      {project.title}
                    </h3>
                    <div className="flex space-x-2">
                      <a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-300"
                      >
                        <FaGithub className="w-4 h-4" />
                      </a>
                      <a
                        href={project.demo}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className={`p-2 text-gray-400 hover:${colors.hoverAccent} transition-colors duration-300`}
                      >
                        <FaExternalLinkAlt className="w-4 h-4" />
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                    <FaCalendarAlt className="w-4 h-4 mr-2" />
                    {project.date}
                  </div>

                  <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                    {project.description}
                  </p>

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.slice(0, 4).map((tech, techIndex) => {
                      const IconComponent = getTechIcon(tech);
                      return (
                        <div
                          key={techIndex}
                          className={`flex items-center space-x-2 px-3 py-1 rounded-full ${colors.background} border border-gray-300 dark:border-gray-600 shadow-lg hover:shadow-xl transition-all duration-300`}
                        >
                          <IconComponent 
                            className="w-3 h-3 dark:[filter:drop-shadow(0_0_0.5px_rgba(255,255,255,0.5))]" 
                            style={{ color: getTechColor(tech) }}
                          />
                          <span className={`text-xs font-medium ${colors.textPrimary}`}>{tech}</span>
                        </div>
                      );
                    })}
                    {project.technologies.length > 4 && (
                      <span className={`px-3 py-1 ${colors.bgAccent}/20 dark:${colors.bgAccent}/30 ${colors.text} text-xs font-medium rounded-full`}>
                        +{project.technologies.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Project Modal */}
          <ProjectModal 
            project={selectedProject}
            isOpen={isModalOpen}
            onClose={closeModal}
          />
        </div>
      </div>
    </section>
  );
};

export default Projects;

